import React, { useEffect, useRef, useState } from 'react';
import { Image } from '..';
import { DropdownItem } from '../types';
import { useOnOutClick } from 'hooks';
import classes from './index.module.scss';

/**
 * Props interface for the DropdownButton component
 *
 * @template T - Type of the value in dropdown items
 */
interface Props<T> {
	/** Button text/label */
	title: string;
	/** Array of dropdown items to display */
	list: DropdownItem<T>[];
	/** Callback function when an item is selected */
	onItemSelect?: (value: T) => void;
	/** Callback function to reset selection */
	onSelectReset?: () => void;
}

/**
 * DropdownButton component
 * Renders a button that opens a dropdown menu when clicked
 * Supports selecting items from the dropdown list
 *
 * @template T - Type of the value in dropdown items (string or number)
 * @param {Props<T>} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const DropdownButton = <T extends string | number>({ list, title, onItemSelect }: Props<T>) => {
	// State for dropdown items
	const [listItems, setListItems] = useState(list);
	// State for dropdown open/closed status
	const [isOpen, setIsOpen] = useState(false);
	// State for currently selected item
	const [selected, setSelected] = useState<typeof list[0]>();
	// Reference to the dropdown container for click outside detection
	const ref = useRef<HTMLDivElement>(null);

	/**
	 * Close the dropdown menu
	 */
	const closeDropdown = () => setIsOpen(false);

	/**
	 * Toggle the dropdown menu open/closed
	 */
	const toggleDropdown = () => setIsOpen((prev) => !prev);

	/**
	 * Update dropdown items when the list prop changes
	 */
	useEffect(() => {
		setListItems(list);
	}, [list]);

	/**
	 * Call onItemSelect callback when selected item changes
	 */
	useEffect(() => {
		if (selected && onItemSelect) {
			onItemSelect(selected.value);
		}
	}, [selected, onItemSelect]);

	/**
	 * Close dropdown when clicking outside
	 * Uses custom hook for detecting clicks outside the component
	 */
	useOnOutClick({ ref, onOutClick: closeDropdown });

	/**
	 * Handle item selection from the dropdown
	 * Updates selected state and closes the dropdown
	 *
	 * @param {typeof list[0]} item - Selected dropdown item
	 */
	const handleItemSelect = (item: typeof list[0]) => {
		setSelected(item);
		closeDropdown();
	};

	return (
		<div ref={ref} className={classes['selectButtonDropdown-container']}>
			{/* Dropdown button/header */}
			<a onClick={toggleDropdown} className={classes['selectButtonDropdown-header']}>
				{/* Show selected item label or default title */}
				<p className={classes['selectButtonDropdown-headerTitle']}>{selected?.label ?? title}</p>
				{/* Dropdown indicator icon */}
				<Image src="icChevron" />
			</a>
			{/* Dropdown menu - only shown when isOpen is true */}
			{isOpen && (
				<ul className={classes['selectButtonDropdown-list']}>
					{/* Map through list items and render each one */}
					{listItems.map((item) => (
						<li key={item.value} className={classes['selectButtonDropdown-listItem']}>
							{/* Clickable item that calls handleItemSelect when clicked */}
							<a onClick={() => handleItemSelect(item)}>{item.label}</a>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

export default DropdownButton;
